import PropTypes from 'prop-types';

/**
 * Reusable chapter wrapper for scrollytelling.
 * Applies state-driven styling and semantics based on position in story.
 *
 * Props:
 *   id: Unique identifier for this chapter
 *   title: Chapter heading
 *   state: 'preview' | 'active' | 'past'
 *   children: Chapter content
 *   className: Additional CSS classes
 */
export default function ElectionStoryChapter({ id, title, state, children, className = '' }) {
  const stateClasses = {
    preview: 'opacity-50 scale-95 motion-safe:transition-all motion-safe:duration-300',
    active: 'opacity-100 scale-100 motion-safe:transition-all motion-safe:duration-300',
    past: 'opacity-75 motion-safe:transition-all motion-safe:duration-300',
  };

  const ariaLive = state === 'active' ? 'polite' : 'off';

  return (
    <section
      id={id}
      className={`
        py-12 px-4 sm:px-6 lg:px-8
        motion-reduce:scale-100 motion-reduce:opacity-100
        ${stateClasses[state]}
        ${className}
      `}
      aria-live={ariaLive}
      data-chapter-state={state}
    >
      <h2 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900 mb-6">{title}</h2>
      <div className="max-w-3xl mx-auto">{children}</div>
    </section>
  );
}

ElectionStoryChapter.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  state: PropTypes.oneOf(['preview', 'active', 'past']).isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

ElectionStoryChapter.defaultProps = {
  className: '',
};
